import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class RequestErrorInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: Subject<any> = new Subject<any>();

  constructor(
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    return next.handle(request).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {

          if (request.url === environment.url + '/auth/login') {
            return throwError(() => error);
          }
          if (request.url.includes('/auth/refresh-token')) {
            if (error.status === 401) {
              this.logoutAndRedirect();
            }
            return throwError(() => error);
          }
          if (request.url.includes('generativelanguage.googleapis.com')) {
            return throwError(() => error);
          }
          if (error.status === 401) {
            if (!this.refreshTokenInProgress) {
              this.refreshTokenInProgress = true;
              this.refreshTokenSubject.next(null);

              const refreshToken = this.authService.getRefreshToken();
              if (!refreshToken) {
                this.refreshTokenInProgress = false;
                this.authService.logout();
                return throwError(() => error);
              }

              return this.authService.refreshUserToken().pipe(
                switchMap((refreshResponse) => {
                  this.refreshTokenInProgress = false;
                  this.refreshTokenSubject.next(refreshResponse);
                  this.authService.saveAuthSession(refreshResponse.response);

                  const accessToken =
                    refreshResponse.response?.token ??
                    localStorage.getItem('token');

                  const modifiedRequest = request.clone({
                    setHeaders: {
                      Authorization: `Bearer ${accessToken}`,
                    },
                  });

                  return next.handle(modifiedRequest);
                }),
                catchError((refreshError) => {
                  this.refreshTokenInProgress = false;
                  if (
                    refreshError instanceof HttpErrorResponse &&
                    refreshError.status === 401
                  ) {
                    this.logoutAndRedirect();
                  } else {
                    this.authService.logout();
                  }
                  return throwError(() => refreshError);
                })
              );
            } else {
              return this.refreshTokenSubject.pipe(
                switchMap((refreshResponse) => {
                  const modifiedRequest = request.clone({
                    setHeaders: {
                      Authorization: `Bearer ${refreshResponse.response?.token}`,
                    },
                  });
                  return next.handle(modifiedRequest);
                }),
                take(1)
              );
            }
          } else {
            let errors = error.error?.errors;
            if (errors && Object.keys(errors)?.length > 0) {
              if (errors?.[Symbol.iterator]) {
                for (let er of errors) {
                  for (let i of errors[er]) {
                    this.toastService.error(i);
                  }
                }
              } else {
                for (let er in errors) {
                  for (let i of errors[er]) {
                    this.toastService.error(i);
                  }
                }
              }
            } else {
              this.toastService.error(error.error?.message || 'An error occurred');
            }
            return throwError(error);
          }
        } else {
          this.toastService.error('Error: ' + error.error.message);
          return throwError(error);
        }
      })
    );
  }

  private logoutAndRedirect() {
    this.refreshTokenInProgress = false;
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }
}
